import React, { useCallback, useEffect, useReducer } from "react";
import { isNil } from "ramda";
import { styled } from "styled-components";

import { Field, FieldLabel, ValidationError } from "affinityUI/form/components";
import { Input } from "affinityUI/form/inputs/input";

import { useAffinityField } from "../../helpers";

const TimeSegmentInput = styled(Input)`
  width: 4rem;
`;

const InputWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const timeSegmentsReducer = (
  segments: { hours?: string; minutes?: string },
  action: { hours?: string; minutes?: string }
) => {
  return { ...segments, ...action };
};

const timeToSegments = (time?: string) => {
  if (isNil(time)) return {};
  const segments = time.split(":");
  return { hours: segments[0] ?? "", minutes: segments[1] ?? "" };
};

const validateTime = (value?: Maybe<string>) =>
  !isNil(value) && !/^([0-9]|[0-1][0-9]|2[0-3]):([0-5][0-9])$/.test(value) ? "Please enter a valid time" : undefined;

export const TimeField: React.FC<React.PropsWithChildren<{ name: string }>> = ({ name, children }) => {
  const [field, { error }, { setError, setValue }] = useAffinityField({ name, validate: validateTime });
  const { value } = field;
  const [segments, setSegments] = useReducer(timeSegmentsReducer, timeToSegments(value));
  const groupId = `field-${name}`;
  const handleHourOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setError(undefined);
      setSegments({ hours: e.target.value });
    },
    [setError]
  );
  const handleMinutesOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setError(undefined);
      setSegments({ minutes: e.target.value });
    },
    [setError]
  );

  useEffect(() => {
    const newValue =
      !isNil(segments.hours) && segments.hours !== "" && !isNil(segments.minutes) && segments.minutes !== ""
        ? `${segments.hours}:${segments.minutes.padStart(2, "0")}`
        : undefined;
    if (newValue !== value) setValue(newValue);
  }, [value, segments, setValue]);

  return (
    <Field role="group" id={name} aria-labelledby={groupId}>
      <FieldLabel id={groupId}>{children}</FieldLabel>
      <InputWrapper>
        <TimeSegmentInput
          placeholder="HH"
          onChange={handleHourOnChange}
          value={segments.hours ?? ""}
          type="number"
          $error={error}
          id={`${name}.hours`}
          name={`${name}.hours`}
          data-testid={`${name}.hours`}
        />
        <span>:</span>
        <TimeSegmentInput
          placeholder="MM"
          onChange={handleMinutesOnChange}
          value={segments.minutes ?? ""}
          type="number"
          $error={error}
          id={`${name}.minutes`}
          name={`${name}.minutes`}
          data-testid={`${name}.minutes`}
        />
      </InputWrapper>
      {error && <ValidationError>{error}</ValidationError>}
    </Field>
  );
};
